/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * 输入框，支持文本输入、选择、地址选择
 */
import './index.scss';

import _ from 'lodash';
import { Component, Prop, Vue } from 'vue-property-decorator';

import IconSvg from '@/components/Icon-svg';
import DictApi from '@/api/dict';

import { OptionItem, getOptionNameByCode } from '@/model/OptionItem';
import { areaPicker } from '@/shared/utils/areaPicker';
import { AddressForm, AreaJsonData } from '@/shared/utils/areaPicker/types';
import { openCommonPicker } from '@/shared/utils/commonPicker';
import { prevent } from '@/shared/utils/prevent';
import { emojRanges, FieldConfig } from './types';

const linkArrowIcon = require('./img/link-arrow.png');

@Component({ name: 'Field' })
export default class Field extends Vue {
  @Prop({
    type: [Number, String, Object],
  })
  private value!: number | string | AddressForm;

  @Prop({
    type: String,
    default: 'text',
  })
  private type!: FieldConfig['type'];

  @Prop(String)
  private unit!: string;

  @Prop(String)
  private label!: string;

  @Prop(String)
  private placeholder!: string;

  @Prop({
    type: Array,
    default: () => [],
  })
  private options!: OptionItem[];

  @Prop({
    type: Array,
    default: () => [],
  })
  private outOptions!: OptionItem[];

  @Prop(String)
  private icon!: string;

  @Prop({
    type: Boolean,
    default: false,
  })
  private disabled!: boolean;

  @Prop()
  private dictApi!: () => Promise<OptionItem[]>;

  @Prop(String)
  private keyProp!: string;

  @Prop(Boolean)
  private sub!: boolean;

  @Prop({
    type: Number,
    default: 64,
  })
  private maxLength!: number;

  @Prop(String)
  private error!: string;

  private dictOptions: OptionItem[] | null = null;

  private areaList: AreaJsonData = Object.create(null);

  private get valueDisplay(): string | number {
    if (this.isSelect && this.mixedOptions) {
      return getOptionNameByCode(this.value as string, this.mixedOptions);
    }

    if (this.isAddress) {
      const address = this.value as AddressForm || {}; // 接口 companyAddress 初始化无值时 返回空对象
      const formatValue = _.uniq([address.province, address.city, address.district]).join(
        ' ',
      );
      return formatValue;
    }

    return this.value as string;
  }

  private set valueDisplay(val: string | number) {
    let _val = val;
    let result: string | number | undefined = val;

    if (this.isLikeNumber) {
      if (this.maxLength && String(val).length > this.maxLength) {
        _val = String(val).slice(0, this.maxLength);
      }
      if (this.type === 'int') {
        _val = String(val).replace(/[^\d]/g, '');
      }

      // 为了对数字类型的输入同样限制长度
      result = _val && (_val as string).length && this.type !== 'numberText'
        ? +_val
        : _val || undefined;

      if (result === this.value) {
        // 为了强制刷新
        this.$emit('input', `${_val}_`);
      }
    }

    // 去掉emoj
    if (new RegExp(emojRanges.join('|'), 'g').test(String(result))) {
      this.$emit('input', result);

      result = _.isString(result)
        ? result.replace(new RegExp(emojRanges.join('|'), 'g'), '')
        : result;
    }

    this.$nextTick(() => {
      this.$emit('input', result);
      this.$emit('change', result);
    });
  }

  private get isLikeNumber() {
    return _.includes(['number', 'int', 'numberText'], this.type);
  }

  private get inputProps() {
    const domProps = {
      type: 'text',
      pattern: '',
    };

    if (
      this.type === 'tel'
      || this.type === 'password'
      || this.type === 'email'
      || this.type === 'number'
    ) {
      domProps.type = this.type;
    }
    if (this.isLikeNumber) {
      domProps.pattern = '[0-9]*';
      domProps.type = 'number';
    }
    return { domProps };
  }

  private get mainPlaceHolder() {
    const action = this.isLink ? '选择' : '输入';
    const typeAction = this.isAddress ? '选择省市区' : '';
    return this.placeholder || typeAction || action + this.label;
  }

  private get isLink() {
    return _.includes(['select', 'address'], this.type);
  }

  private get isSelect() {
    return this.type === 'select';
  }

  private get isAddress() {
    return this.type === 'address';
  }

  private get isPick() {
    return this.type === 'pick';
  }

  private get mixedOptions() {
    if (this.outOptions.length > 0) {
      return this.outOptions;
    }
    return this.dictOptions || this.options;
  }

  public getOptionArr(): OptionItem[] | null {
    return this.dictOptions;
  }

  private async created() {
    if (this.dictApi) {
      this.dictOptions = await this.dictApi();
    }

    // 初始化获取位置信息
    if (this.isAddress) {
      this.areaList = await DictApi.getAddressList();
    }
  }

  private onClickInput(e: Event) {
    if (!this.isLink || this.disabled) {
      return;
    }

    prevent(e);

    if (this.isAddress) {
      areaPicker.open(
        {
          title: this.mainPlaceHolder,
          // companyAddress 字段未返回，无值时，初始化''
          value: this.value as AddressForm ? (this.value as AddressForm).districtCode : '',
          onConfirm: (item) => {
            this.$emit('input', item);
            this.$emit('change');
          },
        },
        this.areaList,
      );
    } else {
      openCommonPicker({
        title: this.mainPlaceHolder,
        options: this.mixedOptions,
        index: this.value
          ? _.findIndex(
            this.mixedOptions,
            (i) => this.getOptionValue(i) === this.value,
          )
          : 0,
        onConfirm: (item: OptionItem) => {
          this.$emit('input', this.getOptionValue(item));
          this.$emit('change');
        },
      });
    }
  }

  private onBlur() {
    this.$emit('blur');
  }

  private onFocus() {
    this.$emit('focus');
  }

  private onPick(option: OptionItem) {
    this.$emit('input', this.getOptionValue(option));
    this.$emit('change', this.getOptionValue(option));
  }

  private getOptionValue(option: OptionItem): string | number {
    if (this.keyProp) {
      return String(_.get(option, this.keyProp) || '');
    }

    return option.code;
  }

  private render() {
    return (
      <div v-class>
        <div v-class="container" class={[{ sub: this.sub }, { errorContainer: this.error }]}>
          <div v-class="label">{this.label}</div>
          <div v-class="field">
            <div
              v-class="field-row"
              onClick={this.onClickInput.bind(this)}
              class={{ link: this.isLink }}
            >
              {!this.isPick ? (
                <input
                  v-class="field-row-input"
                  {...this.inputProps}
                  maxlength={
                    this.type !== 'tel'
                      ? this.maxLength
                      : Math.min(this.maxLength, 20)
                  }
                  placeholder={this.mainPlaceHolder}
                  v-model={this.valueDisplay}
                  disabled={this.disabled}
                  readonly={this.isLink}
                  onBlur={this.onBlur.bind(this)}
                  onFocus={this.onFocus.bind(this)}
                />
              ) : (
                <div v-class={['field-row-picks']}>
                  {_.map(this.mixedOptions, (option) => (
                    <div
                      v-class={[
                        'field-row-picks-item',
                        { active: this.value === this.getOptionValue(option) },
                      ]}
                      onClick={() => this.onPick(option)}
                    >
                      {option.name}
                    </div>
                  ))}
                </div>
              )}
              {this.unit && <span>{this.unit}</span>}
              {/* {this.isLink && <IconSvg v-class='link-arrow' iconClass='link-arrow'/>} */}
              {this.isLink && <img src={linkArrowIcon} v-class="link-arrow" />}
              {this.$slots.icon
                || (this.icon && <IconSvg iconClass={this.icon} />)}
            </div>
            <p v-show={this.error} v-class="error">
              <IconSvg iconClass="alert" />
              <span>{this.error}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }
}
